/**
 * Renderer helpers for reading ~/.aiee/skills through the Electron preload bridge.
 */

export interface DesktopSkillCatalogItem {
    slug: string;
    folder?: string;
    path: string;
    fullPath?: string;
    name?: string;
    description?: string;
    skillId?: string;
    version?: number;
    contentHash?: string;
}

export interface DesktopSkillCatalog {
    root: string;
    items: DesktopSkillCatalogItem[];
}

export function canReadLocalSkills(): boolean {
    return typeof window !== 'undefined' && typeof window.electronAPI?.scanLocalSkills === 'function';
}

export async function scanDesktopSkills(): Promise<DesktopSkillCatalog> {
    if (!canReadLocalSkills() || !window.electronAPI?.scanLocalSkills) {
        throw new Error('Local skill scan is only available in the desktop app');
    }
    return window.electronAPI.scanLocalSkills();
}

export function catalogToInstalledResponse(catalog: DesktopSkillCatalog): {
    slugs: string[];
    items: Array<{
        slug: string;
        folder?: string;
        path: string;
        fullPath?: string;
        skillId?: string;
        version?: number;
        contentHash?: string;
        name?: string;
        description?: string;
    }>;
} {
    const items = (catalog.items || []).map((item) => ({
        slug: item.slug,
        folder: item.folder || item.slug,
        path: item.path || item.fullPath || '',
        fullPath: item.fullPath || item.path || '',
        skillId: item.skillId,
        version: item.version,
        contentHash: item.contentHash,
        name: item.name,
        description: item.description
    })).filter((item) => item.slug);
    return {
        slugs: items.map((item) => item.slug),
        items
    };
}

export async function ackDesktopSkillCatalogScan(
    ack: (resp: { ok: boolean; result?: any; error?: any }) => void
): Promise<void> {
    if (!canReadLocalSkills()) {
        ack({ ok: false, error: 'desktop-scan-unavailable' });
        return;
    }
    try {
        const catalog = await scanDesktopSkills();
        ack({
            ok: true,
            result: {
                success: true,
                status: 0,
                message: JSON.stringify({
                    source: 'desktop',
                    skills: catalogToInstalledResponse(catalog).items
                })
            }
        });
    } catch (error: any) {
        ack({ ok: false, error: error?.message || String(error) });
    }
}

export function canManageLocalSkills(): boolean {
    return typeof window !== 'undefined'
        && typeof window.electronAPI?.installSkillZip === 'function'
        && typeof window.electronAPI?.uninstallSkillFolder === 'function';
}

export async function installDesktopSkill(payload: {
    slug: string;
    zipBase64: string;
    meta?: { skillId?: string; version?: number; contentHash?: string };
}): Promise<string> {
    if (!canManageLocalSkills() || !window.electronAPI?.installSkillZip) {
        throw new Error('Installing a local skill is only available in the desktop app');
    }
    return window.electronAPI.installSkillZip(payload);
}

export async function uninstallDesktopSkill(slug: string): Promise<string> {
    if (!canManageLocalSkills() || !window.electronAPI?.uninstallSkillFolder) {
        throw new Error('Uninstalling a local skill is only available in the desktop app');
    }
    return window.electronAPI.uninstallSkillFolder(slug);
}

export function canOpenLocalSkillFolder(): boolean {
    return typeof window !== 'undefined' && typeof window.electronAPI?.openSkillFolder === 'function';
}

export async function openDesktopSkillFolder(slug: string): Promise<string> {
    if (!canOpenLocalSkillFolder() || !window.electronAPI?.openSkillFolder) {
        throw new Error('Opening a local skill folder is only available in the desktop app');
    }
    return window.electronAPI.openSkillFolder(slug);
}

export async function readDesktopSkillMarkdown(slug: string): Promise<string> {
    if (!window.electronAPI?.readSkillMarkdown) {
        return '';
    }
    try {
        return String(await window.electronAPI.readSkillMarkdown(slug) || '').trim();
    } catch (error) {
        console.warn('[DesktopSkills] Failed to read SKILL.md:', error);
        return '';
    }
}

export function slugFromLocalSkillPath(filePath?: string): string {
    const normalized = String(filePath || '').replace(/\\/g, '/').replace(/\/+$/, '');
    const match = normalized.match(/\/([^/]+)\/SKILL\.md$/i)
        || normalized.match(/\/skills\/([^/]+)$/i);
    return match ? match[1] : '';
}
